import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { ApiError, api } from '@/services/api';
import { authService } from '@/services/auth';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Gate for authenticated routes. Revalidates the session against the backend
 * (`/auth/me`) rather than trusting persisted localStorage — the token is the
 * source of truth, so the login screen shows whenever the session is invalid.
 *
 * Revalidation is deferred until zustand's persist middleware has finished
 * rehydrating from localStorage. That rehydration is asynchronous, so on a hard
 * navigation (`page.goto`) the auth store is briefly empty; firing `/auth/me`
 * before it settles sends a tokenless request that 401s a perfectly valid
 * session and bounces the user to /login. Gating on persist's real hydration
 * signal (not a setTimeout tick guess) makes the check deterministic. Once
 * hydrated, a single genuine failure clears the token and redirects.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, setUser, logout } = useAuthStore();
  const location = useLocation();
  const [hydrated, setHydrated] = useState(() => useAuthStore.persist.hasHydrated());
  const [checkingSession, setCheckingSession] = useState(true);
  const [sessionValid, setSessionValid] = useState<boolean | null>(null);

  // Block until persist has rehydrated the auth store from localStorage.
  useEffect(() => {
    if (hydrated) return;
    const unsub = useAuthStore.persist.onFinishHydration(() => setHydrated(true));
    // Hydration may have completed between the initial read and this subscribe.
    if (useAuthStore.persist.hasHydrated()) setHydrated(true);
    return unsub;
  }, [hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;

    async function validate() {
      setCheckingSession(true);
      setSessionValid(null);
      try {
        const u = await authService.getCurrentUser();
        if (cancelled) return;
        setUser(u);
        setSessionValid(true);
      } catch (err) {
        if (cancelled) return;
        // Only a genuine 401 proves the session is dead. Any other failure —
        // the /auth/me request aborted by a hard navigation tearing down
        // in-flight fetches, a network blip, a 5xx — must NOT clear the token.
        // Doing so is the bounce-to-login bug: an aborted revalidation on the
        // outgoing page would wipe the token the incoming page is about to
        // rehydrate from. In that case leave auth untouched and admit the
        // session while a token is still present.
        if (err instanceof ApiError && err.status === 401) {
          api.setToken(null);
          logout();
          setSessionValid(false);
        } else {
          setSessionValid(Boolean(useAuthStore.getState().accessToken));
        }
      } finally {
        if (!cancelled) setCheckingSession(false);
      }
    }

    validate();
    return () => {
      cancelled = true;
    };
  }, [hydrated, setUser, logout]);

  if (!hydrated || checkingSession || sessionValid === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!sessionValid || !user) {
    // Carry the deep-linked location through so Login can send the user back
    // to where they were trying to go, instead of always landing on '/'.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
